// GtPublicForm — the form people answer, at /f/:token. No sign-in.
// A person's own link (from an email) shows "answering as" and lets them change
// their answers until the form closes. A form's shared link asks for a name,
// an email and consent first, because those answers join a record.
import { useState, useEffect, useCallback } from 'react';
import API_BASE from '../../config';
import gtLogo from '../../brand/main-logo-black.png';
import GtFormFields, { missingRequired } from '../../components/GtFormFields';

function ukDay(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

function Paras({ text, cls }) {
  if (!text) return null;
  return <div className={cls}>{String(text).split(/\n{2,}/).map((p, i) => <p key={i}>{p.split('\n').map((l, j) => (j ? [<br key={j} />, l] : l))}</p>)}</div>;
}

export default function GtPublicForm({ token }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [me, setMe] = useState({ name: '', email: '', consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [banner, setBanner] = useState(null);
  const src = (() => { try { return new URLSearchParams(window.location.search).get('src') || ''; } catch (e) { return ''; } })();

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_BASE + '/api/public/forms/' + encodeURIComponent(token) + (src ? '?src=' + encodeURIComponent(src) : ''));
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setError(body.error || 'This form could not be opened.'); return; }
      setForm(body);
      if (body.answers) setAnswers(body.answers);
    } catch (e) { setError('This form could not be opened. Check your connection and try again.'); }
  }, [token, src]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (form && form.title) document.title = form.title + ' · Generative Talent Co'; }, [form]);

  const set = (key, v) => {
    setAnswers((a) => ({ ...a, [key]: v }));
    if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };

  function firstError(errs) {
    const keys = Object.keys(errs);
    if (!keys.length) return;
    const el = document.getElementById(keys[0].charAt(0) === '_' ? 'gtfp-me' : 'gtff-' + keys[0]);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  async function submit(e) {
    if (e) e.preventDefault();
    setBanner(null);
    const errs = missingRequired(form.questions, answers);
    if (form.mode === 'link') {
      if (!me.name.trim()) errs._name = 'Tell us your name.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(me.email.trim())) errs._email = 'Enter a valid email address.';
      if (!me.consent) errs._consent = 'Please tick this so we can keep your answers.';
    }
    if (Object.keys(errs).length) {
      setErrors(errs); setBanner('A few answers need another look.'); firstError(errs); return;
    }
    setBusy(true);
    try {
      const payload = form.mode === 'link'
        ? { answers, name: me.name, email: me.email, consent: me.consent, src }
        : { answers };
      const res = await fetch(API_BASE + '/api/public/forms/' + encodeURIComponent(token), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.errors) { setErrors(body.errors); firstError(body.errors); }
        setBanner(body.error || 'Your answers could not be saved. Please try again.');
      } else {
        setDone({ thanks: body.thanks || form.thanks || '' });
        if (form.mode === 'personal') setForm((f) => ({ ...f, answered_at: body.answered_at, answers }));
        window.scrollTo(0, 0);
      }
    } catch (err) { setBanner('Your answers could not be sent. Check your connection and try again.'); }
    setBusy(false);
  }

  const shell = (inner) => (
    <div className="gtfp-root">
      <div className="gtfp-wrap">
        <img src={gtLogo} alt="Generative Talent Co" className="gtfp-logo" />
        {inner}
        <p className="gtfp-foot">Generative Talent Co (GTCO Ltd) is responsible for your data. Your answers are added to your record with us and seen by our recruitment team. You can ask us to delete them at any time.</p>
      </div>
    </div>
  );

  if (error) return shell(<div className="gtfp-card gtfp-msg"><h1>Form unavailable</h1><p>{error}</p></div>);
  if (!form) return shell(<div className="gtfp-card gtfp-msg"><div className="gtfp-spin" /><p>Opening the form…</p></div>);

  if (done) {
    return shell(
      <div className="gtfp-card gtfp-done">
        <div className="gtfp-tick">✓</div>
        <h1>Thank you{form.first_name ? ', ' + form.first_name : ''}</h1>
        {done.thanks ? <Paras text={done.thanks} cls="gtfp-thanks" /> : <p className="gtfp-thanks">Your answers are in. We will be in touch if there is a next step.</p>}
        {form.mode === 'personal' && !form.closed && (
          <button type="button" className="gtfp-link" onClick={() => setDone(null)}>Change my answers</button>
        )}
      </div>
    );
  }

  const readOnly = !!form.closed;
  return shell(
    <form className="gtfp-form" onSubmit={submit} noValidate>
      <header className="gtfp-card gtfp-head">
        <div className="gtfp-band" />
        <h1>{form.title}</h1>
        <Paras text={form.intro} cls="gtfp-intro" />
        <div className="gtfp-meta">
          {form.mode === 'personal' && form.first_name && <span className="gtfp-chip">Answering as {form.first_name}</span>}
          {form.closes_at && !form.closed && <span className="gtfp-chip gtfp-chip--soft">Open until {ukDay(form.closes_at)}</span>}
          {form.questions.some((q) => q.required) && <span className="gtfp-req-note"><b>*</b> Required</span>}
        </div>
      </header>

      {form.closed && (
        <div className="gtfp-note">This form has closed.{form.answered_at ? ' These are the answers you gave.' : ''}</div>
      )}
      {!form.closed && form.answered_at && (
        <div className="gtfp-note gtfp-note--ok">You answered on {ukDay(form.answered_at)}. You can change your answers below until the form closes.</div>
      )}

      {form.mode === 'link' && (
        <div className="gtfp-card gtfp-me" id="gtfp-me">
          <div className="gtff-label">About you</div>
          <div className="gtfp-me-grid">
            <label className={errors._name ? 'gtfp-bad' : ''}><span>Full name <i>*</i></span>
              <input className="gtff-input" value={me.name} autoComplete="name" maxLength={200}
                onChange={(e) => { setMe({ ...me, name: e.target.value }); setErrors((x) => ({ ...x, _name: undefined })); }} />
              {errors._name && <em className="gtff-err">{errors._name}</em>}
            </label>
            <label className={errors._email ? 'gtfp-bad' : ''}><span>Email <i>*</i></span>
              <input className="gtff-input" type="email" value={me.email} autoComplete="email" maxLength={320}
                onChange={(e) => { setMe({ ...me, email: e.target.value }); setErrors((x) => ({ ...x, _email: undefined })); }} />
              {errors._email && <em className="gtff-err">{errors._email}</em>}
            </label>
          </div>
        </div>
      )}

      <GtFormFields questions={form.questions} answers={answers} onChange={set} errors={errors} disabled={readOnly} />

      {!readOnly && (
        <div className="gtfp-card gtfp-submit">
          {form.mode === 'link' && (
            <label className={'gtfp-consent' + (errors._consent ? ' gtfp-consent--bad' : '')}>
              <input type="checkbox" checked={me.consent} onChange={(e) => { setMe({ ...me, consent: e.target.checked }); setErrors((x) => ({ ...x, _consent: undefined })); }} />
              <span>I agree that GTCO Ltd may keep these answers with my name and email, and contact me about work that may suit me. I can ask for them to be deleted at any time.</span>
            </label>
          )}
          {errors._consent && <div className="gtff-err">{errors._consent}</div>}
          {banner && <div className="gtfp-banner">{banner}</div>}
          <button type="submit" className="gtfp-primary" disabled={busy}>
            {busy ? 'Sending…' : form.answered_at ? 'Save my answers' : 'Submit'}
          </button>
        </div>
      )}
    </form>
  );
}
